import { reactive, nextTick } from "vue";
import i18n from "~/plugins/i18n";
import { showAlert } from "~/plugins/AlertPlugin";

// Laravel 422 errors keyed by field path ("email", "sta.sta_location").
// Inputs show them via :error-messages="$fieldError('email')".
export const serverErrors = reactive({});
const renderedKeys = new Set();

export function fieldError(key)
{
  renderedKeys.add(key);
  return serverErrors[key] ?? [];
}

export function clearServerErrors()
{
  for (const key of Object.keys(serverErrors))
  {
    delete serverErrors[key];
  }
}

// .field-error marks non-input elements (e.g. a section) that render $fieldError messages.
export function scrollToFirstError()
{
  // offsetParent is null for hidden elements (inactive tabs, closed dialogs).
  [...document.querySelectorAll(".v-input--error, .field-error")]
    .find((el) => el.offsetParent !== null)
    ?.scrollIntoView({ behavior: "smooth", block: "center" });
}

// Marks the inputs bound via $fieldError(key) red and scrolls to the first one.
// Used for 422 responses and for page-level checks: showFieldErrors({ pro_timelines: "..." }).
// Only messages that no visible input shows fall back to the alert, so nothing is lost.
export async function showFieldErrors(errors)
{
  clearServerErrors();
  renderedKeys.clear();
  for (const key of Object.keys(errors))
  {
    serverErrors[key] = [].concat(errors[key]);
  }
  await nextTick();
  scrollToFirstError();
  const unshown = Object.keys(serverErrors)
    .filter((key) => !renderedKeys.has(key))
    .map((key) => serverErrors[key][0]);
  if (unshown.length)
  {
    showAlert({ icon: "error", title: "error_alert_title", html: unshown.join("<br />") });
  }
}

// Client-side rules for Vuetify inputs: :rules="[$rules.required]"
export const rules = {
  required: (v) => (v !== null && v !== undefined && String(v).trim() !== "" && !(Array.isArray(v) && !v.length)) || i18n.global.t("validation_required"),
  email: (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || i18n.global.t("validation_email"),
  max: (n) => (v) => v === null || v === undefined || String(v).length <= n || i18n.global.t("validation_max", { max: n }),
  number: (v) => v === null || v === undefined || v === "" || /^\d+([.,]\d+)?$/.test(String(v).trim()) || i18n.global.t("validation_number"),
};

// Validates a v-form ref and scrolls to the first invalid input. Returns true when valid.
export async function validateForm(form)
{
  if (!form) return true;
  const { valid } = await form.validate();
  if (!valid)
  {
    await nextTick();
    scrollToFirstError();
  }
  return valid;
}

export const FormErrorsPlugin = {
  install: (app) =>
  {
    app.config.globalProperties.$fieldError = fieldError;
    app.config.globalProperties.$rules = rules;
  },
};
